import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";

// A yes/no question asked in the page, not with window.confirm: a browser's own
// dialog is out of the page, so a person's assistive tools style it as they
// do not the rest, and a test driver (agent-browser, Playwright) has to know
// to answer it before it appears, or the page hangs on it. This one is part
// of the page — role="dialog", its buttons real buttons — and Esc or a click
// outside cancels it.

export type ConfirmOptions = {
  /** what the confirming button says; the action, never "OK" */
  action: string;
  /** a destructive action: its button is drawn as one */
  danger?: boolean;
};

type Asking = ConfirmOptions & { message: string; resolve: (yes: boolean) => void };

/**
 * `[ask, dialog]`: `ask(message, options)` shows the question and resolves
 * true for the action, false for Cancel, Esc or a click outside; `dialog` is
 * the element to render, nothing while no question is open.
 */
export function useConfirm(): [(message: string, options: ConfirmOptions) => Promise<boolean>, ReactNode] {
  const [asking, setAsking] = useState<Asking | null>(null);

  const ask = useCallback(
    (message: string, options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setAsking({ ...options, message, resolve });
      }),
    [],
  );

  const answer = useCallback(
    (yes: boolean) => {
      asking?.resolve(yes);
      setAsking(null);
    },
    [asking],
  );

  return [ask, asking ? <ConfirmDialog asking={asking} onAnswer={answer} /> : null];
}

function ConfirmDialog({ asking, onAnswer }: { asking: Asking; onAnswer: (yes: boolean) => void }) {
  const cancel = useRef<HTMLButtonElement>(null);

  // Esc is this question's alone: heard before anything else on the page
  // and taken, so a popover still open under it — Invite, while Stop sharing
  // asks — stays open, and a dialog under it too (components/Modal)
  const keys = useModalKeys(() => onAnswer(false));
  useEffect(() => {
    // the safe answer has the focus, so Enter on a destructive question cancels
    cancel.current?.focus();
  }, []);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onAnswer(false)}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-message" onKeyDown={keys}>
        <p id="confirm-message">{asking.message}</p>
        <div className="row modal-actions">
          <button ref={cancel} onClick={() => onAnswer(false)}>
            {t("common:cancel")}
          </button>
          <button className={asking.danger ? "danger" : "primary"} onClick={() => onAnswer(true)}>
            {asking.action}
          </button>
        </div>
      </div>
    </div>
  );
}
